import React, { useMemo, useState, useCallback } from 'react';
import {
    Show,
    SimpleShowLayout,
    useRecordContext,
    useRedirect,
    useRefresh,
    useTranslate,
    usePermissions,
} from 'react-admin';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    AccountBalanceWallet,
    ArrowBack,
    Cancel,
    CheckCircle,
    Error as ErrorIcon,
    Fingerprint,
    Payment,
    Person,
    Receipt,
    Schedule,
    Undo,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { RefundTopUpDialog, TopUpRecord } from './RefundTopUpDialog';
import { TruncatedId } from './TruncatedId';

// -- shared styles (mirrors AppTransactionsShow.tsx so the pages feel identical) --
const detailsTableSx = {
    tableLayout: 'fixed',
    width: '100%',
    '& .MuiTableCell-root': {
        px: { xs: 1, sm: 2 },
        py: { xs: 1, sm: 1.25 },
        verticalAlign: 'top',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
    },
};

const detailRowSx = {
    py: 1,
    px: 1,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'rgba(0, 0, 0, 0.01)',
};

const detailLabelSx = {
    fontSize: '0.72rem',
    color: 'text.secondary',
    fontWeight: 600,
    mb: 0.5,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
};

const detailValueSx = {
    fontSize: '0.875rem',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
};

const wrappedChipSx = {
    maxWidth: '100%',
    height: 'auto',
    '& .MuiChip-label': {
        display: 'block',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: 1.2,
        py: 0.5,
    },
};

const getStatusColor = (status: string) => {
    switch (status) {
        case 'completed': return 'success';
        case 'pending': return 'warning';
        case 'failed': return 'error';
        case 'cancelled': return 'default';
        case 'refunded': return 'warning';
        default: return 'default';
    }
};

const hasRefundPermission = (permissions: any): boolean => {
    if (!permissions) return false;
    const list: string[] = Array.isArray(permissions)
        ? permissions
        : Array.isArray(permissions?.permissions)
            ? permissions.permissions
            : [];
    return list.includes('app-topups:admin:all') || list.includes('*:*:*');
};

// --- Page Header ---
const PageHeader: React.FC = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();

    if (!record) return null;

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed': return <CheckCircle />;
            case 'pending': return <Schedule />;
            case 'failed': return <ErrorIcon />;
            case 'cancelled': return <Cancel />;
            case 'refunded': return <Undo />;
            default: return <AccountBalanceWallet />;
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: 2, sm: 4 },
            flexWrap: 'wrap',
            gap: 2,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                <IconButton
                    onClick={() => redirect('/app-topups')}
                    size="large"
                    sx={{
                        flexShrink: 0,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        },
                    }}
                >
                    <ArrowBack />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            fontSize: { xs: '1.25rem', sm: '2.125rem' },
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                        }}
                    >
                        {translate('resources.app-topups.show.title', { _: 'Top-up detail' })}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                        <Chip
                            icon={getStatusIcon(String(record.status))}
                            label={translate(`resources.app-topups.statuses.${record.status}`, { _: record.status })}
                            color={getStatusColor(String(record.status)) as any}
                            size="small"
                            sx={wrappedChipSx}
                        />
                        <TruncatedId id={record.id} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// --- Transaction Info Card ---
const TopUpInfo: React.FC = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    if (!record) return null;

    const rows: Array<{ label: string; value: React.ReactNode }> = [
        {
            label: translate('resources.app-topups.fields.id', { _: 'Transaction ID' }),
            value: <TruncatedId id={record.id} />,
        },
        {
            label: translate('resources.app-topups.fields.status', { _: 'Status' }),
            value: (
                <Chip
                    label={translate(`resources.app-topups.statuses.${record.status}`, { _: record.status })}
                    color={getStatusColor(String(record.status)) as any}
                    size="small"
                    sx={wrappedChipSx}
                />
            ),
        },
        {
            label: translate('resources.app-topups.fields.amount', { _: 'Amount' }),
            value: (
                <Typography sx={{ ...detailValueSx, color: 'success.main', fontWeight: 700 }}>
                    + HKD {Number(record.amount).toFixed(2)}
                </Typography>
            ),
        },
        {
            label: translate('resources.app-topups.fields.timestamp', { _: 'Date' }),
            value: record.timestamp ? format(new Date(String(record.timestamp)), 'PPpp') : '-',
        },
        {
            label: translate('resources.app-topups.fields.paymentMethod', { _: 'Payment method' }),
            value: record.payment_method || '-',
        },
        {
            label: translate('resources.app-topups.fields.referenceType', { _: 'Reference type' }),
            value: record.reference_type || '-',
        },
        {
            label: translate('resources.app-topups.fields.referenceId', { _: 'Reference ID' }),
            value: <TruncatedId id={record.reference_id} />,
        },
    ];

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <Box sx={{
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        borderRadius: 2,
                        backgroundColor: 'primary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                    }}>
                        <Receipt sx={{ color: 'primary.main' }} />
                    </Box>
                    <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.app-topups.show.sectionTopUpInfo', { _: 'Top-up information' })}
                    </Typography>
                </Box>
                {isMobile ? (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                        {rows.map((r, i) => (
                            <Box key={i} sx={detailRowSx}>
                                <Typography sx={detailLabelSx}>{r.label}</Typography>
                                <Box sx={detailValueSx}>{r.value}</Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                        <Table size="small" sx={detailsTableSx}>
                            <TableBody>
                                {rows.map((r, i) => (
                                    <TableRow key={i}>
                                        <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}>
                                            <strong>{r.label}</strong>
                                        </TableCell>
                                        <TableCell sx={{ wordBreak: 'break-word' }}>{r.value}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
};

// --- User Info Card ---
const UserInfo: React.FC = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    if (!record) return null;

    const rows: Array<{ label: string; value: React.ReactNode }> = [
        {
            label: translate('resources.app-topups.fields.userId', { _: 'User ID' }),
            value: <TruncatedId id={record.user_id} />,
        },
        {
            label: translate('resources.app-topups.fields.userName', { _: 'Name' }),
            value: record.user?.name || '-',
        },
        {
            label: translate('resources.app-topups.fields.userEmail', { _: 'Email' }),
            value: record.user?.email || '-',
        },
    ];

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <Box sx={{
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        borderRadius: 2,
                        backgroundColor: 'info.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                    }}>
                        <Person sx={{ color: 'info.main' }} />
                    </Box>
                    <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.app-topups.show.sectionUser', { _: 'User' })}
                    </Typography>
                </Box>
                {isMobile ? (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                        {rows.map((r, i) => (
                            <Box key={i} sx={detailRowSx}>
                                <Typography sx={detailLabelSx}>{r.label}</Typography>
                                <Box sx={detailValueSx}>{r.value}</Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                        <Table size="small" sx={detailsTableSx}>
                            <TableBody>
                                {rows.map((r, i) => (
                                    <TableRow key={i}>
                                        <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}>
                                            <strong>{r.label}</strong>
                                        </TableCell>
                                        <TableCell sx={{ wordBreak: 'break-word' }}>{r.value}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
};

// --- Payment Details Card (conditional) ---
const PaymentDetails: React.FC = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record || !record.payment_details) return null;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <Box sx={{
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        borderRadius: 2,
                        backgroundColor: 'warning.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                    }}>
                        <Payment sx={{ color: 'warning.main' }} />
                    </Box>
                    <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.app-topups.show.sectionPaymentDetails', { _: 'Payment details' })}
                    </Typography>
                </Box>
                <Paper sx={{ bgcolor: 'grey.50', p: 2 }}>
                    <pre style={{
                        margin: 0,
                        fontSize: '0.75rem',
                        wordBreak: 'break-all',
                        overflowWrap: 'anywhere',
                        whiteSpace: 'pre-wrap',
                    }}>
                        {JSON.stringify(record.payment_details, null, 2)}
                    </pre>
                </Paper>
            </CardContent>
        </Card>
    );
};

// --- Admin Notes Card (conditional) ---
const AdminNotes: React.FC = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record || !record.admin_notes) return null;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" mb={2}>
                    <Box sx={{
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        borderRadius: 2,
                        backgroundColor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                    }}>
                        <Fingerprint sx={{ color: 'grey.700' }} />
                    </Box>
                    <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.app-topups.fields.adminNotes', { _: 'Admin notes' })}
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {String(record.admin_notes)}
                </Typography>
            </CardContent>
        </Card>
    );
};

// --- Refund Action Card ---
const RefundActionCard: React.FC = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const refresh = useRefresh();
    const { permissions } = usePermissions();
    const canRefund = useMemo(() => hasRefundPermission(permissions), [permissions]);
    const [open, setOpen] = useState(false);

    const handleSuccess = useCallback(() => {
        refresh();
    }, [refresh]);

    if (!record) return null;
    if (!canRefund) return null;
    if (record.status !== 'completed') return null;
    if (record.type !== 'topup') return null;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" mb={2}>
                    <Box sx={{
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        borderRadius: 2,
                        backgroundColor: 'warning.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                    }}>
                        <Undo sx={{ color: 'warning.main' }} />
                    </Box>
                    <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.app-topups.show.sectionRefund', { _: 'Refund' })}
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {translate('resources.app-topups.show.refundHint', {
                        _: "Return the full amount to the user's original payment method via BBMSL and deduct from their wallet.",
                    })}
                </Typography>
                <Button
                    variant="contained"
                    color="warning"
                    startIcon={<Undo />}
                    onClick={() => setOpen(true)}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    {translate('resources.app-topups.actions.refund', { _: 'Refund' })}
                </Button>
            </CardContent>
            <RefundTopUpDialog
                open={open}
                topUp={record as TopUpRecord}
                onClose={() => setOpen(false)}
                onSuccess={handleSuccess}
            />
        </Card>
    );
};

// --- Aggregator ---
const TopUpDetails: React.FC = () => (
    <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', minWidth: 0 }}>
        <PageHeader />
        <TopUpInfo />
        <UserInfo />
        <PaymentDetails />
        <AdminNotes />
        <RefundActionCard />
    </Box>
);

export const AppTopUpsShow: React.FC = () => (
    <Show
        resource="app-topups"
        title={false}
        actions={false}
        component="div"
        sx={{
            backgroundColor: 'transparent',
            maxWidth: '100%',
            '& .RaShow-main': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
                maxWidth: '100%',
            },
        }}
    >
        <SimpleShowLayout
            sx={{
                maxWidth: '100%',
                '& .MuiPaper-root': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
                '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                    px: { xs: 0, sm: 2 },
                    py: { xs: 1, sm: 2 },
                },
                padding: { xs: 0, sm: 'inherit' },
            }}
        >
            <TopUpDetails />
        </SimpleShowLayout>
    </Show>
);

export default AppTopUpsShow;
